import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { ReactTyped } from "react-typed";
import "./Contact.css";
import { ENDPOINTS, apiFetch } from "../api/api";
import useFetch from "../hooks/useFetch";
import { useTranslation } from "react-i18next";
import "../i18n";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

function Contact() {
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [formData, setFormData]   = useState({ name: "", email: "", subject: "", budget: "", message: "" });
  const [status, setStatus]       = useState(null);
  const [activeFAQ, setActiveFAQ] = useState(null);
  const [showFull, setShowFull]   = useState(false);

  const { data: faqs }         = useFetch(ENDPOINTS.faq);
  const { data: contactInfos } = useFetch(ENDPOINTS.contactInfo);
  const { data: socials }      = useFetch(ENDPOINTS.socialLinks);

  const BUDGETS = ["< KSh 50,000","KSh 50,000 – 250,000","KSh 250,000 – 1,000,000","KSh 1,000,000+","Let's discuss"];
  const FAQS         = faqs         || [];
  const CONTACT_INFO = contactInfos || [];
  const SOCIALS      = socials      || [];

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!executeRecaptcha) {
      console.error("reCAPTCHA not ready");
      setStatus("error");
      return;
    }

    setStatus("sending");

    try {
      const captchaToken = await executeRecaptcha("contact_form");

      await apiFetch(ENDPOINTS.contact_messages, {
        method: "POST",
        body: JSON.stringify({
          ...formData,
          createdAt: new Date().toISOString(),
          captchaToken,
        }),
      });

      setStatus("success");
      setFormData({ name: "", email: "", subject: "", budget: "", message: "" });
    } catch (err) {
      console.error("[Contact] Form submission failed:", err);
      setStatus("error");
    }
  };

  return (
    <div className="ct-page">
      <section className="ct-hero">
        <div className="ct-hero-bg">
          <div className="ct-orb ct-orb--1"></div><div className="ct-orb ct-orb--2"></div><div className="ct-hero-grid"></div>
        </div>
        <div className="ct-hero-inner">
          <div className="ct-info">
            <span className="ct-eyebrow"><i className="fa-solid fa-paper-plane"></i> {t("contact.eyebrow")}</span>
            <h1 className="ct-hero-title">
              {!showFull ? (
                <ReactTyped strings={[t("contact.title")]} typeSpeed={48} showCursor={false} loop={false} onComplete={() => setShowFull(true)} />
              ) : (
                <>{t("contact.title")}</>
              )}
            </h1>
            <p className="ct-hero-subtitle">{t("contact.desc")}</p>
            <div className="ct-info-cards">
              {CONTACT_INFO.map((item) => (
                <div className="ct-info-card" key={item.id}>
                  <div className="ct-info-icon"><i className={item.icon}></i></div>
                  <div className="ct-info-text">
                    <span className="ct-info-label">{item.label}</span>
                    {item.lines.map((line, i) => <span key={i} className="ct-info-value">{line}</span>)}
                  </div>
                </div>
              ))}
            </div>
            <div className="ct-socials">
              <span className="ct-socials-label">Follow us:</span>
              {SOCIALS.map((s) => (
                <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer" className="ct-social-btn" aria-label={s.label} style={{ "--hover-color": s.color }}>
                  <i className={s.icon}></i>
                </a>
              ))}
            </div>
            <div className="ct-response-badge">
              <span className="ct-response-dot"></span>
              <span>Average response time: <strong>under 4 hours</strong></span>
            </div>
          </div>

          <div className="ct-form-wrap">
            <div className="ct-form-header">
              <h2 className="ct-form-title">Send us a Message</h2>
              <p className="ct-form-subtitle">We'd love to hear about your project.</p>
            </div>
            {status === "success" ? (
              <div className="ct-success">
                <div className="ct-success-icon"><i className="fa-solid fa-circle-check"></i></div>
                <h3 className="ct-success-title">Message Sent!</h3>
                <p className="ct-success-desc">{t("contact.success")}</p>
                <button className="ct-success-btn" onClick={() => setStatus(null)}>Send Another Message</button>
              </div>
            ) : (
              <form className="ct-form" onSubmit={handleSubmit} noValidate>
                {status === "error" && (
                  <div style={{ color: "#ff6b6b", marginBottom: "1rem", fontSize: "0.875rem" }}>
                    <i className="fa-solid fa-triangle-exclamation"></i> Failed to send your message. Please try again or email us directly.
                  </div>
                )}
                <div className="ct-form-row">
                  <div className="ct-field">
                    <label className="ct-label">{t("contact.name")} <span>*</span></label>
                    <div className="ct-input-wrap">
                      <i className="fa-solid fa-user ct-input-icon"></i>
                      <input type="text" name="name" className="ct-input" placeholder={t("contact.namePlaceholder")} value={formData.name} onChange={handleChange} required />
                    </div>
                  </div>
                  <div className="ct-field">
                    <label className="ct-label">{t("contact.email")} <span>*</span></label>
                    <div className="ct-input-wrap">
                      <i className="fa-solid fa-envelope ct-input-icon"></i>
                      <input type="email" name="email" className="ct-input" placeholder={t("contact.emailPlaceholder")} value={formData.email} onChange={handleChange} required />
                    </div>
                  </div>
                </div>
                <div className="ct-form-row">
                  <div className="ct-field">
                    <label className="ct-label">{t("contact.subject")} <span>*</span></label>
                    <div className="ct-input-wrap">
                      <i className="fa-solid fa-tag ct-input-icon"></i>
                      <input type="text" name="subject" className="ct-input" placeholder={t("contact.subjectPlaceholder")} value={formData.subject} onChange={handleChange} required />
                    </div>
                  </div>
                  <div className="ct-field">
                    <label className="ct-label">Budget Range</label>
                    <div className="ct-input-wrap">
                      <i className="fa-solid fa-dollar-sign ct-input-icon"></i>
                      <select name="budget" className="ct-input ct-select" value={formData.budget} onChange={handleChange}>
                        <option value="">Select budget...</option>
                        {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="ct-field">
                  <label className="ct-label">{t("contact.message")} <span>*</span></label>
                  <div className="ct-input-wrap ct-textarea-wrap">
                    <i className="fa-solid fa-message ct-input-icon ct-textarea-icon"></i>
                    <textarea name="message" className="ct-input ct-textarea" placeholder={t("contact.messagePlaceholder")} rows={5} value={formData.message} onChange={handleChange} required />
                  </div>
                  <span className="ct-char-count">{formData.message.length} / 1000</span>
                </div>
                <label className="ct-terms">
                  <input type="checkbox" className="ct-checkbox" required />
                  <span>I agree to the <NavLink to="/privacy" className="ct-terms-link">Privacy Policy</NavLink> and <NavLink to="/terms" className="ct-terms-link">Terms of Service</NavLink></span>
                </label>
                <button type="submit" className={`ct-submit-btn ${status === "sending" ? "ct-submit-btn--loading" : ""}`} disabled={status === "sending"}>
                  {status === "sending" ? <><span className="ct-submit-spinner"></span> {t("contact.sending")}</> : <><i className="fa-solid fa-paper-plane"></i> {t("contact.send")}</>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="ct-map-section">
        <div className="ct-map-inner">
          <div className="ct-map-text">
            <span className="ct-section-eyebrow">Find Us</span>
            <h2 className="ct-section-title">We're Based in Nairobi</h2>
            <p className="ct-section-subtitle">Operating across Africa and serving clients globally — 100% remote friendly.</p>
            <div className="ct-map-stats">
              <div className="ct-map-stat"><span className="ct-map-stat-value">🇰🇪</span><span className="ct-map-stat-label">Nairobi HQ</span></div>
              <div className="ct-map-stat"><span className="ct-map-stat-value">5+</span><span className="ct-map-stat-label">Countries Served</span></div>
              <div className="ct-map-stat"><span className="ct-map-stat-value">100%</span><span className="ct-map-stat-label">Remote Capable</span></div>
            </div>
          </div>
          <div className="ct-map-embed">
            <div className="ct-map-placeholder">
              <i className="fa-solid fa-map-location-dot"></i>
              <span>Nairobi, Kenya</span>
              <a href="https://maps.google.com/?q=Nairobi,Kenya" target="_blank" rel="noopener noreferrer" className="ct-map-link">
                Open in Google Maps <i className="fa-solid fa-arrow-up-right-from-square"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="ct-faq-section">
        <div className="ct-container">
          <div className="ct-section-header">
            <span className="ct-section-eyebrow">Got Questions?</span>
            <h2 className="ct-section-title">Frequently Asked Questions</h2>
            <p className="ct-section-subtitle">Quick answers to the most common things people ask us.</p>
          </div>
          <div className="ct-faq-grid">
            {FAQS.map((faq, i) => (
              <div key={faq.id || i} className={`ct-faq-item ${activeFAQ === i ? "ct-faq-item--open" : ""}`} onClick={() => setActiveFAQ(activeFAQ === i ? null : i)}>
                <div className="ct-faq-question">
                  <span className="ct-faq-q-text">{faq.q || faq.question}</span>
                  <div className="ct-faq-toggle"><i className={`fa-solid ${activeFAQ === i ? "fa-minus" : "fa-plus"}`}></i></div>
                </div>
                <div className="ct-faq-answer"><p className="ct-faq-answer-text">{faq.a || faq.answer}</p></div>
              </div>
            ))}
          </div>
          <div className="ct-faq-cta">
            <p>Still have questions?</p>
            <NavLink to="/contact" className="ct-faq-cta-link"><i className="fa-solid fa-comment"></i> Chat with us directly</NavLink>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;